import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Globals } from '../../globals';
import { Group } from '../groups/groups.interfaces';
import {
  EmailExistsResponse,
  Installer,
  Register,
  ScannedValve,
  User,
  UserImp,
} from './users.interfaces';

@Injectable({
  providedIn: 'root',
})

/**
 * User REST calls
 */
export class UsersService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);

  /**
   * Get all users
   */
  getUsers(): Observable<UserImp[]> {
    const url = `${this.globals.apiRoot}/jwtusers`;
    return this.http
      .get<UserImp[]>(url)
      .pipe(
        map((data) => data.map((item) => Object.assign(new UserImp(), item))),
      );
  }

  /**
   * Get all installers
   */
  getInstallers(): Observable<UserImp[]> {
    const url = `${this.globals.apiRoot}/jwtinstallers`;
    return this.http
      .get<UserImp[]>(url)
      .pipe(
        map((data) => data.map((item) => Object.assign(new UserImp(), item))),
      );
  }

  /**
   * Get user by id
   * @param id User id
   */
  getUser(id: number): Observable<UserImp> {
    const url = `${this.globals.apiRoot}/jwtusers/${id}`;
    return this.http
      .get<UserImp>(url)
      .pipe(map((item) => Object.assign(new UserImp(), item)));
  }

  /**
   * Get the settings for a user and his shared accounts
   * Currently only a number, which is the limit of the accounts that can be added
   */
  getUserSharedAccountSettings(): Observable<number> {
    const url = `${this.globals.apiRoot}/jwtusers/settings/sharedaccounts`;
    return this.http.get<number>(url);
  }

  /**
   * Get user Groups
   */
  // DEPRECATED - TODO: update in pwg
  getUserGroups(id: number): Observable<Group[]> {
    const url = `${this.globals.apiRoot}/jwtusers/${id}/groups`;
    return this.http.get<Group[]>(url);
  }
  // DEPRECATED - TODO: update in pwg
  getUserGroup(id: number, groupId: number): Observable<Group> {
    const url = `${this.globals.apiRoot}/jwtusers/${id}/groups/${groupId}`;
    return this.http.get<Group>(url);
  }

  /**
   * Update user data
   * @param user User object
   */
  updateUser(user: UserImp): Observable<void> {
    const url = `${this.globals.apiRoot}/jwtusers/${user.id}`;
    return this.http.put<void>(url, user);
  }

  updatePassword(userId: number, newPassword: string, oldPassword: string) {
    const url = `${this.globals.apiRoot}/jwtusers/${userId}/password`;
    return this.http.put<void>(url, {
      password: newPassword,
      currentPassword: oldPassword,
    });
  }

  /**
   * Verify if a user can register to an installer
   * @param installer
   */
  canRegisterInstaller(installer: Installer) {
    const url = `${this.globals.apiRoot}/jwtregister/installer/canregister`;
    return this.http.put<number>(url, installer);
  }

  /**
   * user register methods
   */
  emailExists(email: string) {
    const url = `${this.globals.apiRoot}/jwtregister/v2/${email}`;
    return this.http.get<EmailExistsResponse>(url);
  }

  resetPassword(email: string, source?: string) {
    if (source === undefined || source === null || source === '') {
      const url = `${this.globals.apiRoot}/jwtregister/${email}`;
      return this.http.post<boolean>(url, null);
    }
    const url = `${this.globals.apiRoot}/jwtregister/${email}/${source}`;
    return this.http.post<boolean>(url, null);
  }

  /**
   * Request a password reset
   * @param email email address for which to request a password reset
   */
  requestPasswordReset(email: string) {
    const url = `${this.globals.apiRoot}/jwtregister/${email}`;
    return this.http.post<boolean>(url, null);
  }

  /**
   * Verify whether the password can still be reset
   * @param key resetkey for user
   */
  canResetPassword(token: string) {
    const url = `${this.globals.apiRoot}/jwtregister/${token}/reset`;
    return this.http.get<boolean>(url);
  }

  /**
   * Reset the password
   * @param token reset token for user
   */
  resetPasswordByToken(token: string, user: User) {
    const url = `${this.globals.apiRoot}/jwtregister/${token}/reset`;
    return this.http.put<boolean>(url, user);
  }

  createUser(user: Register | User) {
    const url = `${this.globals.apiRoot}/jwtregister`;
    return this.http.put(url, user);
  }

  createInstallerUser(user: Installer) {
    const url = `${this.globals.apiRoot}/jwtregister/installer`;
    return this.http.put(url, user);
  }

  updateInstallerUser(user: Installer) {
    const url = `${this.globals.apiRoot}/jwtregister/existing`;
    return this.http.put(url, user);
  }

  /**
   *  Add Valve
   */
  addValve(userId: number, scannedValve: ScannedValve) {
    const url = `${this.globals.apiRoot}/jwtusers/${userId}/addvalve`;
    return this.http.put(url, scannedValve);
  }

  /**
   * Activation methods
   */

  /**
   * Check if the user is activated
   * @param activationKey Key to activate the user with
   */
  isActivated(activationKey: string): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtusers/${activationKey}/isActivated`;
    return this.http.get<boolean>(url);
  }

  /**
   * Check if the user can be activated
   * @param activationKey Key to activate the user with
   */
  hasToBeActivated(activationKey: string): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtusers/${activationKey}/hasToBeActivated`;
    return this.http.get<boolean>(url);
  }

  /**
   * Activate a user
   * @param activationKey Key to activate the user with
   * @param password New password for the user
   */
  activate(activationKey: string, user: Register | null): Observable<boolean> {
    const url = `${this.globals.apiRoot}/jwtusers/${activationKey}/activate`;
    return this.http.put<boolean>(url, user);
  }

  resendActivationMail(userId: number) {
    const url = `${this.globals.apiRoot}/jwtusers/${userId}/resendactivation`;
    return this.http.get(url);
  }

  /**
   * Delete a users
   */
  deleteUser(userId: number) {
    const url = `${this.globals.apiRoot}/jwtusers/${userId}`;
    return this.http.delete(url);
  }

  /**
   * Send logging of app to backend
   */
  sendLogs(userId: number, logs: any) {
    const url = `${this.globals.apiRoot}/jwtusers/${userId}/logs`;
    return this.http.post<boolean>(url, logs);
  }
}
